import {
  builtInPostalResolver,
  describeGrowingSeason,
  describePostalFailure,
  describeWinter,
  detectPostalCode,
  nativeRegionLabel,
  type PostalResolver,
  type SiteConditionsPatch,
  type SiteLocalization,
} from '@/localization';
import { clear, el, setClass } from './dom';
import { icon } from './Icons';

/**
 * **Localize Site** — one field, one button, and afterwards four lines.
 *
 * ## Why it is this small
 *
 * A ZIP code is the only thing a user should have to know. Everything the app actually runs on —
 * hardiness zone, frost dates, region, heat band, latitude — is derived from it, and showing all of
 * that by default would turn a two-second action into a weather station. What comes back is the
 * shortest set of facts that lets someone confirm the app understood where they are. Anything more is
 * available on the plants themselves, where it changes a decision.
 *
 * ## Built from the HUD's own parts
 *
 * Every element here is a class the rest of the HUD already defines — `lg-input`, `lg-button`,
 * `lg-section__title`, `lg-legend`, `lg-hint`, `lg-chip`. That is not tidiness for its own sake: a
 * panel with a private idea of what an input looks like reads as something bolted on, and it stops
 * tracking the rest of the interface the first time a token changes. The only bespoke rule left is
 * the row layout, which nothing else needed.
 *
 * ## Two independent operations
 *
 * Typing a ZIP and asking the browser where you are are separate jobs with separate guards, because
 * one of them can be slow: a geolocation prompt that is never answered used to leave a single shared
 * flag raised, which disabled the pin *and* made Localize silently do nothing. See
 * `localization/BrowserLocation.ts`, which now guarantees that call settles.
 *
 * Rendering only. Every value shown comes from `localization/`, and pressing Localize calls back to
 * the application, which owns the profile and the resolver — this section holds none of either.
 */

export interface LocalizeSectionDeps {
  /** The site's profile, or null when it has never been localized. */
  localization: () => SiteLocalization | null;
  /**
   * Adopt a profile, or clear it.
   *
   * `auto` marks a profile the app detected at startup rather than one the user asked for. The
   * distinction matters downstream: an automatic profile must not mark the document dirty, or merely
   * opening the app would mint a saved "Untitled site".
   */
  onLocalize: (localization: SiteLocalization | null, options?: { auto?: boolean }) => void;
  /** Change irrigation or shelter without re-resolving the postcode. */
  onConditions: (conditions: SiteConditionsPatch) => void;
  /** Injectable so a networked lookup can replace the built-in table without touching this file. */
  resolver?: PostalResolver;
}

export class LocalizeSection {
  readonly root: HTMLElement;

  private readonly input: HTMLInputElement;
  private readonly button: HTMLButtonElement;
  private readonly locateButton: HTMLButtonElement;
  private readonly message: HTMLElement;
  private readonly summary: HTMLElement;
  /** Raised while a postcode is being resolved. */
  private busy = false;
  /** Raised while the browser is being asked where it is — deliberately not the same flag. */
  private locating = false;

  constructor(private readonly deps: LocalizeSectionDeps) {
    this.input = el('input', {
      class: 'lg-input lg-localize__input',
      attrs: {
        type: 'text',
        inputmode: 'numeric',
        maxlength: '10',
        placeholder: 'ZIP code',
        'aria-label': 'Site ZIP code',
        spellcheck: 'false',
      },
      on: {
        keydown: (event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            void this.localize();
          }
        },
        input: () => this.setMessage(''),
      },
    });

    this.locateButton = el('button', {
      class: 'lg-button lg-localize__locate',
      html: icon('pin', 14),
      title: 'Use my location — asks the browser for permission',
      attrs: { type: 'button', 'aria-label': 'Use my location' },
      on: { click: () => void this.useMyLocation() },
    });

    this.button = el('button', {
      class: 'lg-button lg-button--primary',
      text: 'Localize',
      attrs: { type: 'button' },
      on: { click: () => void this.localize() },
    });

    this.message = el('p', { class: 'lg-legend lg-localize__message', attrs: { hidden: true } });
    this.summary = el('div', { class: 'lg-localize__summary' });

    this.root = el(
      'div',
      { class: 'lg-localize' },
      el('div', { class: 'lg-localize__row' }, this.input, this.locateButton, this.button),
      this.message,
      this.summary,
    );

    this.refresh();
    // Only if permission was granted in an earlier session — this must never raise a prompt on load.
    void this.autoLocalize();
  }

  /**
   * Localize the site from the browser's own position, silently, at startup.
   *
   * Only when it costs no permission prompt, so on a browser that has never granted location this
   * does nothing at all and the panel shows its red "not localized yet" alert instead. That is the
   * honest pair of outcomes: either the app really does know where you are, or it says plainly that
   * it does not. What it must never do is put a plausible-looking ZIP in the box and let someone
   * apply a New Jersey climate to a garden in Oregon.
   *
   * Marked `auto` on the way out. A profile the *app* chose is not evidence the user made a site
   * worth keeping, so it must not be what turns a blank session into a saved document — see
   * `Application.localizeSite`.
   */
  private async autoLocalize(): Promise<void> {
    if (this.deps.localization() !== null || this.input.value !== '') return;
    const detected = await detectPostalCode({ prompt: false });
    if (detected === undefined) return;
    // Re-checked after the await: the user may have typed or localized while it was in flight, and
    // their choice outranks the browser's.
    if (this.deps.localization() !== null || this.input.value !== '') return;
    this.input.value = detected;
    await this.localize({ auto: true });
  }

  /** The explicit ask. Prompts, resolves, and localizes in one press. */
  private async useMyLocation(): Promise<void> {
    if (this.locating) return;
    this.locating = true;
    this.locateButton.disabled = true;
    this.setMessage('Finding your location…', 'info');
    let detected: string | undefined;
    try {
      detected = await detectPostalCode({ prompt: true });
    } finally {
      // Always released, whatever the browser did with its prompt.
      this.locating = false;
      this.locateButton.disabled = false;
    }
    if (detected === undefined) {
      this.setMessage('Could not read a US ZIP code from your location — type one instead.');
      return;
    }
    this.input.value = detected;
    this.setMessage('');
    await this.localize();
  }

  /** Redraw from the current profile — after localizing, and after a project is opened. */
  refresh(): void {
    const localization = this.deps.localization();
    // Empty when the site has no profile. There is no default ZIP: see `PostalZones.ts` for why a
    // plausible-looking guess in this box is worse than an empty one.
    this.input.value = localization?.postalCode ?? '';
    this.renderSummary(localization);
  }

  private setMessage(text: string, tone: 'error' | 'info' = 'error'): void {
    this.message.textContent = text;
    this.message.hidden = text === '';
    setClass(this.message, 'is-error', tone === 'error');
  }

  private async localize(options: { auto?: boolean } = {}): Promise<void> {
    if (this.busy) return;
    const raw = this.input.value.trim();
    if (raw === '') {
      // An empty field is how you say "forget where this is", which is a legitimate thing to want
      // and should not require a separate button to do it with.
      this.deps.onLocalize(null);
      this.setMessage('Site is no longer localized.', 'info');
      this.renderSummary(null);
      return;
    }

    this.busy = true;
    this.button.disabled = true;
    try {
      const resolver = this.deps.resolver ?? builtInPostalResolver;
      const result = await resolver.resolve(raw);
      if (result.failure !== undefined || !result.localization) {
        this.setMessage(describePostalFailure(result.failure ?? 'not-in-table'));
        return;
      }
      this.setMessage('');
      this.deps.onLocalize(result.localization, { auto: options.auto === true });
      this.renderSummary(result.localization);
    } finally {
      this.busy = false;
      this.button.disabled = false;
    }
  }

  /**
   * The three facts, then where they came from.
   *
   * No place line: the panel head this sits under already carries the name and the zone, and a
   * summary that restated them would spend its first line saying what the user just read.
   *
   * Confidence is stated rather than implied — a ZIP district is an estimate for the middle of a
   * postal area, and a summary reading like a survey would be the wrong kind of confident. The same
   * word appears here as in the data (`estimated`), following the convention `geo/SiteData.ts` set
   * for simulated parcels.
   */
  private renderSummary(localization: SiteLocalization | null): void {
    clear(this.summary);
    if (!localization) {
      this.summary.appendChild(
        el(
          'div',
          { class: 'lg-hint' },
          el('p', {
            text:
              'Not localized. Plants are shown against the catalog’s reference climate, and no ' +
              'suitability is claimed either way.',
          }),
        ),
      );
      return;
    }

    const rows: [string, string][] = [
      ['Winter', describeWinter(localization)],
      ['Growing season', describeGrowingSeason(localization)],
      [
        'Regional ecology',
        localization.ecoregion
          ? `${localization.ecoregion.name} · ${nativeRegionLabel(localization.nativeRegion)} flora`
          : `${nativeRegionLabel(localization.nativeRegion)} flora`,
      ],
    ];
    for (const [label, value] of rows) {
      this.summary.appendChild(el('h3', { class: 'lg-section__title', text: label }));
      this.summary.appendChild(el('p', { class: 'lg-legend', text: value }));
    }

    this.summary.appendChild(
      el('p', {
        class: 'lg-legend lg-localize__source',
        text: `${localization.source.label} · ${localization.source.confidence} for this ZIP district`,
      }),
    );

    this.summary.appendChild(this.conditions(localization));
  }

  /**
   * Irrigation and shelter — the facts a postcode cannot know.
   *
   * They sit under the summary because they modify it: shelter really does move the effective zone
   * half a step, and the plant badges change the moment it is ticked. Kept to two, because these are
   * the two that change an answer rather than the two that are easiest to add.
   */
  private conditions(localization: SiteLocalization): HTMLElement {
    const current = localization.conditions ?? {};
    const chips = el('div', { class: 'lg-chips' });
    chips.appendChild(
      this.chip('Irrigated', current.irrigated === true, () =>
        this.deps.onConditions({ irrigated: current.irrigated === true ? undefined : true }),
      ),
    );
    for (const shelter of ['sheltered', 'exposed'] as const) {
      chips.appendChild(
        this.chip(shelter === 'sheltered' ? 'Sheltered' : 'Exposed', current.shelter === shelter, () =>
          this.deps.onConditions({ shelter: current.shelter === shelter ? undefined : shelter }),
        ),
      );
    }

    return el(
      'div',
      { class: 'lg-localize__conditions' },
      el('h3', { class: 'lg-section__title', text: 'Site conditions' }),
      chips,
      el('p', {
        class: 'lg-legend',
        text: 'A sheltered position is worth about half a zone; an exposed one costs the same.',
      }),
    );
  }

  private chip(label: string, active: boolean, onClick: () => void): HTMLElement {
    return el('button', {
      class: `lg-chip${active ? ' is-active' : ''}`,
      text: label,
      attrs: { type: 'button', 'aria-pressed': active },
      on: { click: onClick },
    });
  }
}
